'use client';

import { ChangeEvent, useEffect, useState } from 'react';

import useCollectionTagAutoComplete from './useCollectionTagAutoComplete';

import { Collection, CollectionStatus } from '@/shared/types';

interface CollectionModalFormProps {
  defaultCollectionData?: Collection;
}

const useCollectionModalForm = ({
  defaultCollectionData,
}: CollectionModalFormProps) => {
  const [status, setStatus] = useState<CollectionStatus | null>(null);
  const [title, setTitle] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [errorState, setErrorState] = useState({
    title: { isError: false, message: '' },
    status: { isError: false, message: '' },
  });

  const {
    tagQuery,
    selectedTag,
    staticTagItems,
    searchedTagData,
    setTagQuery,
    handleTagInput: tagInput,
    handleTagDelete,
    setSelectedTag,
  } = useCollectionTagAutoComplete({ enabled: true });

  const deleteEmojis = (value: string) => {
    return value.replace(
      /[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]+/gu,
      ''
    );
  };

  const handleStatusChange = (status: CollectionStatus) => {
    setStatus(status);
    if (errorState.status) {
      setErrorState((prev) => ({
        ...prev,
        status: { isError: false, message: '' },
      }));
    }
  };

  const handleTagInput = (value: string) => {
    tagInput(value);
  };

  const handleTitleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const sanitizedValue = deleteEmojis(e.target.value);

    setTitle(sanitizedValue);
    if (errorState.title && e.target.value.trim() !== '') {
      setErrorState((prev) => ({
        ...prev,
        title: { isError: false, message: '' },
      }));
    }
  };

  const handleDescriptionChange = (e: ChangeEvent<HTMLInputElement>) => {
    const sanitizedValue = deleteEmojis(e.target.value);
    setDescription(sanitizedValue);
  };

  const validateForm = () => {
    const isStatusError = !status;
    const isTitleError = title === '';

    if (isStatusError) {
      setErrorState((prev) => ({
        ...prev,
        status: { ...prev.status, isError: true },
      }));

      return false;
    }

    if (isTitleError) {
      setErrorState((prev) => ({
        ...prev,
        title: { isError: true, message: '컬렉션 제목을 입력해 주세요.' },
      }));
    }

    return !(isStatusError || isTitleError);
  };

  useEffect(() => {
    if (defaultCollectionData) {
      setStatus(
        defaultCollectionData.isPublic
          ? CollectionStatus.PUBLIC
          : CollectionStatus.PRIVATE
      );
      setTitle(defaultCollectionData.title);
      setDescription(defaultCollectionData.description);
      setSelectedTag(defaultCollectionData.hashTags.map((tag) => tag.name));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultCollectionData]);

  useEffect(() => {
    if (errorState.title.isError) {
      document.getElementById('title')?.scrollIntoView({
        block: 'center',
      });
      return;
    }
  }, [errorState]);

  return {
    value: { status, title, description, selectedTag },
    onChange: {
      status: handleStatusChange,
      title: handleTitleChange,
      description: handleDescriptionChange,
      tag: { input: handleTagInput, delete: handleTagDelete },
    },
    tagData: {
      query: tagQuery,
      setQuery: setTagQuery,
      selectedItems: selectedTag,
      staticItems: staticTagItems,
      legacyItems: searchedTagData || [],
    },
    error: errorState,
    validateForm,
    setErrorState,
  };
};

export default useCollectionModalForm;
